import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Form, Input, Button, Modal, Space, Divider } from "antd";
import {
  CheckCircleOutlined,
  AndroidOutlined,
  AppleOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import { useSetLocalAdminPasswordMutation } from "./setLocalAdminPassword.service";
import { toast } from "react-toastify";
import "../login/style.css";
import "./style.css";

const SetLocalAdminPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const previewSuccess = searchParams.get("previewSuccess") === "1";

  const [setPassword, { isLoading }] = useSetLocalAdminPasswordMutation();
  const [showSuccessDialog, setShowSuccessDialog] = useState(previewSuccess);
  const [userEmail, setUserEmail] = useState(email);

  const goToAdminLogin = () => {
    setShowSuccessDialog(false);
    navigate("/login");
  };

  const handleSubmit = async (values) => {
    if (values.password !== values.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      await setPassword({
        token,
        email,
        password: values.password,
      }).unwrap();

      toast.success("Password set successfully!");
      setUserEmail(email);
      setShowSuccessDialog(true);
    } catch (error) {
      toast.error(error.data?.message || "Failed to set password");
    }
  };

  return (
    <>
      <div className="signincontainer signup-page auth-page">
        <div className="saas-card auth-card">
          <div className="auth-brand">
            <div className="auth-logo-wrap">
              <img src="/mark1.jpg" alt="SFBB Pro" />
            </div>
          </div>

          <div className="auth-heading">Set your password</div>
          <p className="auth-copy">
            You’ve been invited as a Location Administrator. Create a password
            to continue.
          </p>

          <Form
            form={form}
            layout="vertical"
            className="auth-form"
            requiredMark={false}
            onFinish={handleSubmit}
            autoComplete="off"
            initialValues={{ email }}
          >
            <Form.Item name="email" label="Email">
              <Input className="auth-input" size="large" disabled />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Please enter your password" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input.Password
                placeholder="Create a password"
                className="auth-input"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm password"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder="Re-enter password"
                className="auth-input"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Button
              htmlType="submit"
              size="large"
              block
              loading={isLoading}
              className="signup-cta-btn"
            >
              Set password
            </Button>
          </Form>

          <div className="auth-footer-note">
            This invite link expires in 7 days
          </div>
        </div>
      </div>

      <Modal
        open={showSuccessDialog}
        onCancel={goToAdminLogin}
        footer={null}
        width={400}
        centered
        closable={false}
        maskClosable={false}
        className="set-password-success-modal"
      >
        <div className="set-password-success">
          <div className="set-password-success-icon">
            <CheckCircleOutlined />
          </div>

          <div className="set-password-success-title">Password set</div>
          <p className="set-password-success-copy">
            Next, log in to the <strong>admin website</strong> to finish setup
            (fridge/freezer wizard). Then you can use the mobile app.
          </p>

          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Button
              type="primary"
              icon={<DesktopOutlined />}
              size="large"
              block
              onClick={goToAdminLogin}
              className="set-password-primary-btn"
            >
              Go to admin website
            </Button>

            <Divider plain className="set-password-divider">
              Then download the app
            </Divider>

            <Button
              href="https://play.google.com/store/apps/details?id=com.sfbbpro.app"
              target="_blank"
              icon={<AndroidOutlined />}
              size="large"
              block
              className="set-password-play-btn"
            >
              Google Play Store
            </Button>

            <Button
              href="https://apps.apple.com/gb/app/sfbb-pro/id6749208075"
              target="_blank"
              icon={<AppleOutlined />}
              size="large"
              block
              className="set-password-apple-btn"
            >
              Apple App Store
            </Button>
          </Space>

          <div className="set-password-success-email">
            Use {userEmail || "your email"} to log in
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SetLocalAdminPassword;
